import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { User, Session, AuthError } from '@supabase/supabase-js';
import { supabase } from '../lib/supabase';
import bcrypt from 'bcryptjs';

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<{ error: AuthError | null }>;
  signUp: (email: string, password: string, username?: string, phoneNumber?: string) => Promise<{ error: AuthError | null }>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
};

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const initializeAuth = async () => {
      // Check localStorage for saved session FIRST
      const savedUser = localStorage.getItem('audome_user');
      const savedSession = localStorage.getItem('audome_session');
      
      if (savedUser && savedSession) {
        try {
          const parsedUser = JSON.parse(savedUser);
          const parsedSession = JSON.parse(savedSession);
          
          setUser(parsedUser);
          setSession(parsedSession);
          // Restored session from localStorage
        } catch (err) {
          console.error('Error parsing saved session:', err);
          localStorage.removeItem('audome_user');
          localStorage.removeItem('audome_session');
        }
      }
      
      // Then check Supabase auth (fallback)
      const { data: { session } } = await supabase.auth.getSession();
      if (session && !savedUser) {
        setSession(session);
        setUser(session?.user ?? null);
      }
      
      setLoading(false);
    };

    initializeAuth();

    // Listen for auth changes
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      if (session) {
        setSession(session);
        setUser(session?.user ?? null);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const signIn = async (email: string, password: string) => {
    
    // Get user by email only
    const { data: userData, error: userError } = await supabase
      .from('users')
      .select('*')
      .eq('email', email)
      .maybeSingle();

    

    if (userError || !userData) {
      console.error('❌ User not found:', { userError, email });
      return { error: { message: 'بيانات الدخول غير صحيحة' } as AuthError };
    }
    
    // Check password - support both plain text (for migration) and hashed
    let passwordValid = false;
    
    // Try bcrypt compare first (for encrypted passwords)
    if (userData.password_hash.startsWith('$2')) {
      passwordValid = await bcrypt.compare(password, userData.password_hash);
      console.log('🔐 Bcrypt comparison result:', passwordValid);
    } else {
      // Plain text comparison (for non-encrypted passwords)
      passwordValid = userData.password_hash === password;
      console.log('🔓 Plain text comparison result:', passwordValid);
    }
    
    if (!passwordValid) {
      console.error('❌ Password mismatch');
      return { error: { message: 'بيانات الدخول غير صحيحة' } as AuthError };
    }
    
    console.log('✅ Sign in successful for:', userData.email);

    // Update last login
    await supabase
      .from('users')
      .update({ last_login: new Date().toISOString() })
      .eq('id', userData.id);

    // Create a mock user object for compatibility
    const mockUser = {
      id: userData.id,
      email: userData.email,
      created_at: userData.created_at,
    } as User;

    const mockSession = { user: mockUser } as Session;

    setUser(mockUser);
    setSession(mockSession);
    
    // Save to localStorage for persistence
    localStorage.setItem('audome_user', JSON.stringify(mockUser));
    localStorage.setItem('audome_session', JSON.stringify(mockSession));
    console.log('💾 Session saved to localStorage');

    return { error: null };
  };

  const signUp = async (email: string, password: string, username?: string, phoneNumber?: string) => {
    try {
      // Check if user already exists
      const { data: existingUser } = await supabase
        .from('users')
        .select('id')
        .eq('email', email)
        .maybeSingle(); // Use maybeSingle() instead of single() to avoid 406 error

      if (existingUser) {
        return { error: { message: 'البريد الإلكتروني مستخدم بالفعل' } as AuthError };
      }

      // Create new user directly in our users table
      // Generate unique username by adding random suffix if not provided
      const finalUsername = username || `${email.split('@')[0]}_${Math.random().toString(36).substring(2, 8)}`;
      
      // Hash the password with bcrypt
      const hashedPassword = await bcrypt.hash(password, 10);
      console.log('🔐 Password hashed successfully');
      
      const userData = {
        email,
        username: finalUsername,
        password_hash: hashedPassword, // Properly hashed password
        phone_number: phoneNumber ? `+973${phoneNumber}` : null,
        country: 'Bahrain',
      };
      
      console.log('📝 Attempting to create user with data:', { ...userData, password_hash: '***' });
      
      const { data: newUser, error: insertError } = await supabase
        .from('users')
        .insert(userData)
        .select()
        .single();

      if (insertError) {
        console.error('❌ Signup error details:', insertError);
        return { error: { message: insertError.message || 'فشل في إنشاء الحساب' } as AuthError };
      }

      // Add initial 50 credits with 15-day expiry to credit_lots (NEW SYSTEM)
      await supabase.rpc('add_credits', {
        p_user_id: newUser.id,
        p_amount: 50,
        p_expires_in_days: 15,
        p_source: 'signup_bonus',
        p_reference: 'كريدت ترحيبي للمستخدم الجديد'
      });

      // Create a mock user object for compatibility
      const mockUser = {
        id: newUser.id,
        email: newUser.email,
        created_at: newUser.created_at,
      } as User;

      const mockSession = { user: mockUser } as Session;

      setUser(mockUser);
      setSession(mockSession);
      
      // Save to localStorage for persistence
      localStorage.setItem('audome_user', JSON.stringify(mockUser));
      localStorage.setItem('audome_session', JSON.stringify(mockSession));
      console.log('💾 Session saved to localStorage after signup');

      return { error: null };
    } catch (err) {
      return { error: { message: 'حدث خطأ غير متوقع' } as AuthError };
    }
  };

  const signOut = async () => {
    setUser(null);
    setSession(null);
    
    // Clear localStorage
    localStorage.removeItem('audome_user');
    localStorage.removeItem('audome_session');
    console.log('🗑️ Session cleared from localStorage');
  };

  return (
    <AuthContext.Provider value={{ user, session, loading, signIn, signUp, signOut }}>
      {children}
    </AuthContext.Provider>
  );
};

